import { atom } from "jotai";
import {
    activeFinanceMetricsAtom,
    activeFinLabMetricsAtom,
    financeMonthlyQueryAtom,
    financeQuarterlyQueryAtom,
    financeYearlyQueryAtom,
    financialHighlightsAsOfDateAtom,
    finLabMonthlyQueryAtom,
    finLabQuarterlyQueryAtom,
    finLabYearlyQueryAtom,
    incomeStatementResolutionAtom,
} from "../../../metrics.state";
import { FinanceLabField, formatFinanceLabValue } from "@/entities/FinanceLab";
import { getMetric } from "../../../utils/getMetric";
import {
    getAllAvailablePeriods,
    getPeriodColumns,
    getPeriodsForWindow,
    getUniquePeriods,
    getYTDPeriods,
    sortPeriods,
} from "../../../utils/periods";
import { HeaderWithTooltip } from "../../../components/HeaderWithTooltip";
import {
    calculateDelta,
    getDeltaLabel,
    getDeltaPeriodCount,
    getPeriodSlices,
} from "../../../utils/deltas";
import { ColDef } from "ag-grid-enterprise";
import { DeltaCellRenderer } from "../DeltaCellRenderer";
import { RowConfigWithMetric, SummaryRow } from "../SummaryFinancials.types";
import { rowConfig } from "./rowConfig";
import { INCOME_STATEMENT_LABELS } from "../../../metrics.const";
import { filterMetricsByLabels } from "../../../metrics.state";

export const ISSummaryMetricsAtom = atom<RowConfigWithMetric[]>((get) => {
    const financeMetrics = get(activeFinanceMetricsAtom);
    const finLabMetrics = get(activeFinLabMetricsAtom);

    const unified = {
        ...finLabMetrics,
        ...financeMetrics,
    };

    const labels = Array.from(INCOME_STATEMENT_LABELS);
    const metrics = filterMetricsByLabels(unified, labels);
    return rowConfig.map((row) => {
        return {
            metric: getMetric(
                metrics,
                row.codes[0] as string,
                row.codes[1] as FinanceLabField,
            ),
            ...row,
        };
    });
});

export const ISSummaryPeriodsAtom = atom<{
    periods: string[];
    tablePeriods: string[];
}>((get) => {
    const metricResolution = get(incomeStatementResolutionAtom);
    const deltaPeriodCount = getDeltaPeriodCount(metricResolution.window || "");
    const asOfDate = get(financialHighlightsAsOfDateAtom);

    console.log("metric resolution", metricResolution);

    if (!asOfDate) {
        return { periods: [], tablePeriods: [] };
    }

    if (!["all", "ytd"].includes(metricResolution.window || "")) {
        const periods = sortPeriods(
            getPeriodsForWindow(
                asOfDate,
                metricResolution.periodKind,
                deltaPeriodCount * 2 || 1,
            ),
        );

        return {
            periods,
            tablePeriods: periods.slice(-deltaPeriodCount),
        };
    }

    const metrics = get(ISSummaryMetricsAtom);
    const periods = getAllAvailablePeriods(
        getUniquePeriods(
            metrics.map((m) => m.metric).filter((m) => m !== undefined),
        ),
        asOfDate,
    );
    if (
        metricResolution.periodKind === "yearly" &&
        metricResolution.window === "all"
    ) {
        periods.pop();
    }

    return {
        periods,
        tablePeriods: metricResolution.window?.includes("ytd")
            ? getYTDPeriods(periods, asOfDate)
            : periods,
    };
});

export interface PeriodColumn {
    field: string;
    headerName: string;
    headerComponent?: any;
    headerComponentParams?: any;
}

// Column definitions for the income statement summary table
export const ISSummaryColumnAtom = atom<ColDef[]>((get) => {
    const metricResolution = get(incomeStatementResolutionAtom);
    const periods = get(ISSummaryPeriodsAtom).tablePeriods;
    const periodColumns = getPeriodColumns(periods);

    return [
        {
            field: "label",
            headerName: "",
            flex: 1.5,
            pinned: "left",
            cellClass: "heading-s",
        },
        ...periodColumns,
        {
            field: "popChange",
            maxWidth: 150,
            pinned: "right",
            headerName: getDeltaLabel(metricResolution.window || ""),
            headerComponent: HeaderWithTooltip,
            cellRenderer: DeltaCellRenderer,
            headerComponentParams: {
                tooltipText:
                    "Shows the % difference between the latest two available periods. Eg. if the last closed period is Q1-25, the delta shows the difference between Q1-25 and Q4-24.",
            },
        },
    ];
});

export const ISSummaryDataAtom = atom<SummaryRow[]>((get) => {
    const metricResolution = get(incomeStatementResolutionAtom);
    const deltaPeriodCount = getDeltaPeriodCount(metricResolution.window || "");
    const rows = get(ISSummaryMetricsAtom);
    const { periods, tablePeriods } = get(ISSummaryPeriodsAtom);
    console.log("IS rows", rows);

    return rows.map((r) => {
        const row: SummaryRow = { ...r, previousSlice: [], currentSlice: [] };
        const returnBlankRow = () => {
            tablePeriods.forEach((period) => {
                row[period] = "-";
            });
            row.popChange = null;
            return row;
        };

        if (!r.metric) return returnBlankRow();

        const valuesByPeriod = new Map(
            periods.map((p) => [
                p,
                r.metric?.values.filter((value) => value.period === p).pop()
                    ?.value,
            ]),
        );
        const slices = getPeriodSlices(
            deltaPeriodCount || 1,
            Array.from(valuesByPeriod.entries()).filter(
                (entry): entry is [string, number] => entry[1] !== undefined,
            ),
        );
        const delta = calculateDelta(
            slices?.current || [],
            slices?.previous || [],
        );

        if (slices && delta !== null) {
            row.popChange = delta;
            row.previousSlice = slices.previous;
            row.currentSlice = slices.current;
        }
        tablePeriods.forEach((period) => {
            row[period] =
                valuesByPeriod.get(period) !== undefined
                    ? formatFinanceLabValue(
                          valuesByPeriod.get(period),
                          row.fieldType,
                      )
                    : "-";
        });

        return row;
    });
});

export const isLoadingAtom = atom<boolean>((get) => {
    const resolution = get(incomeStatementResolutionAtom);
    const isFinLabQueryAtom =
        resolution.periodKind === "quarterly"
            ? finLabQuarterlyQueryAtom
            : resolution.periodKind === "yearly"
              ? finLabYearlyQueryAtom
              : finLabMonthlyQueryAtom;

    const isFinanceQueryAtom =
        resolution.periodKind === "quarterly"
            ? financeQuarterlyQueryAtom
            : resolution.periodKind === "yearly"
              ? financeYearlyQueryAtom
              : financeMonthlyQueryAtom;

    return (
        get(isFinLabQueryAtom).isLoading || get(isFinanceQueryAtom).isLoading
    );
});
